/**
 * useHotkeySettings - Organization-scoped hotkey settings from database
 *
 * Fetches hotkey bindings from prod_settings.hotkeys JSONB column and
 * merges them over DEFAULT_HOTKEY_MAP to produce a complete binding map.
 *
 * Features:
 * - Optimistic updates for instant settings page feedback
 * - Automatic rollback on server errors
 * - Merges DB config over defaults (handles new actions added later)
 * - 5-minute staleTime (hotkey settings rarely change)
 */

import { useCallback, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import type { HotkeyConfig } from '@threadheads/shared';
import { mergeHotkeyConfig, type HotkeyMap } from './hotkey-types';

interface UpdateResult {
  success: boolean;
  error?: string;
}

/**
 * Hook to fetch and update hotkey settings from the database.
 *
 * @returns Object containing:
 * - hotkeyMap: Resolved bindings (defaults merged with DB config)
 * - rawConfig: Raw DB config (null if no custom config)
 * - isLoading: True during initial data fetch
 * - isError: True if fetch failed
 * - updateHotkeys: Function to save new config
 * - isUpdating: True while an update is in progress
 */
export function useHotkeySettings() {
  const utils = trpc.useUtils();

  const query = trpc.productionSettings.getHotkeySettings.useQuery(undefined, {
    staleTime: 5 * 60 * 1000, // 5 minutes — settings rarely change
    refetchOnWindowFocus: false,
  });

  const updateMutation = trpc.productionSettings.updateHotkeySettings.useMutation({
    // Optimistic update for instant UI feedback
    onMutate: async (newInput) => {
      await utils.productionSettings.getHotkeySettings.cancel();

      const previousData = utils.productionSettings.getHotkeySettings.getData();

      // Optimistically update the cache
      utils.productionSettings.getHotkeySettings.setData(undefined, {
        hotkeys: newInput.hotkeys,
      });

      return { previousData };
    },
    // Rollback on error
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        utils.productionSettings.getHotkeySettings.setData(undefined, context.previousData);
      }
    },
    // Refetch after success to ensure consistency
    onSettled: () => {
      utils.productionSettings.getHotkeySettings.invalidate();
    },
  });

  /**
   * Save new hotkey configuration.
   *
   * @param hotkeys - Full hotkey config to save
   * @returns Promise resolving to result object with success/error
   */
  const updateHotkeys = useCallback(
    async (hotkeys: HotkeyConfig): Promise<UpdateResult> => {
      try {
        await updateMutation.mutateAsync({ hotkeys });
        return { success: true };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to save hotkey settings. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [updateMutation],
  );

  // Merge DB config over defaults to produce the complete binding map.
  // Memoized to prevent re-render cascades — a new object every render would
  // wipe unsaved edits in HotkeySettingsSection's sync useEffect.
  const rawConfig = query.data?.hotkeys ?? null;
  const hotkeyMap: HotkeyMap = useMemo(
    () => mergeHotkeyConfig(rawConfig as HotkeyConfig | null),
    [rawConfig],
  );

  return {
    /** Resolved bindings (defaults merged with DB config) */
    hotkeyMap,
    /** Raw DB config (null if no custom config) */
    rawConfig,
    /** True during initial data fetch */
    isLoading: query.isLoading,
    /** True if fetch failed */
    isError: query.isError,
    /** Save new hotkey configuration */
    updateHotkeys,
    /** True while an update is in progress */
    isUpdating: updateMutation.isPending,
    /** Last mutation error (if any) */
    lastError: updateMutation.error?.message,
  };
}
